import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core'
import { MatTooltip } from '@angular/material/tooltip'

import {
  GRID_VIEW_POSITION_INDEX,
  IS_GRID_VIEW_HIDDEN,
  TAG_TYPE_OPTIONS
} from 'src/app/constants/internationalized-constants-en'
import { ZoneBoxModel } from 'src/app/models'
import { SvgConverterComponent } from '../svg-converter/svg-converter.component'
import { EvirLanguageDictionary, ZoneLayoutModel } from '../tree/models/tree.model'
import { LanguageDictionaryHandlingService } from '../tree/services/language-dictionary-handling/language-dictionary-handling.service'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-grid-zone-box',
  templateUrl: './grid-zone-box.component.html',
  styleUrls: ['./grid-zone-box.component.scss'],
})
export class GridZoneBoxComponent {

  @ViewChild('svgBackground') svgBackground: SvgConverterComponent

  @Input() svgSource: string
  @Input() languageDictionary: EvirLanguageDictionary
  @Input() set zoneMapData(data: ZoneLayoutModel) {
    if (data && data.assetViewGrid && data.assetViewGrid.length === 2) {
      this._zoneData = data
      this.zoneMapWidth = data.assetViewGrid[GRID_VIEW_POSITION_INDEX.POSITION_X]
      this.zoneMapHeight = data.assetViewGrid[GRID_VIEW_POSITION_INDEX.POSITION_Y]
      this.rebuildGridZone()
    }
  }
  @Input() set inspectionTypes(data: string[]) {
    if (data) {
      this.selectedInspectionTypes = data ? data : []
      this.rebuildGridZone()
    }
  }
  @Output() preparingData = new EventEmitter()
  @Output() dropValue = new EventEmitter()
  @Output() hoveringBox = new EventEmitter()
  @Output() componentInit = new EventEmitter()

  items: Array<ZoneBoxModel> = []

  backgroundWidth: number = 196
  zoneMapWidth: number = 7
  backgroundHeight: number
  zoneMapHeight: number

  selectedInspectionTypes: string[]
  _zoneData: ZoneLayoutModel

  MAX_ZONE_BOX_SIZE: number = 28
  MAX_ZONE_DOT_SIZE: number = 24

  zoneBoxHeight: number = this.MAX_ZONE_BOX_SIZE
  zoneBoxWidth: number = this.MAX_ZONE_BOX_SIZE
  zoneBoxActiveSize: number = this.MAX_ZONE_DOT_SIZE

  isDragging: boolean = false
  isHovering: boolean = false
  customTooltipClasses: string[] = ['multi-line-tooltip', 'custom-position-tooltip']

  keyboardDragSourceIndex: number | null = null
  keyboardDragTargetIndex: number | null = null

  private pendingFocusIndex: number | null = null

  @ViewChildren('boxItemEl') boxItemEls: QueryList<ElementRef<HTMLElement>>

  constructor(
    private langDictionaryService: LanguageDictionaryHandlingService,
    private cdRef: ChangeDetectorRef,
    private translateService: TranslateService,
  ) { }

  /** ECT2-806: Currently the grid being used (behind the map view image) is based on what numbers have been entered within the edit modal for that config.
   *  We remove those input fields and make the grid size more fixed.
   *  Fixed width, dynamic rows based on image loaded. */
  get isAssetViewGridHidden(): boolean {
    return IS_GRID_VIEW_HIDDEN
  }

  // prepare for drag and drop feature
  drop(event: CdkDragDrop<any>) {
    if (event &&
      event.isPointerOverContainer &&
      event.container &&
      event.container.data &&
      event.previousContainer &&
      event.previousContainer.data) {

      this.swapItems(event.previousContainer.data.index, event.container.data.index)

      // Keep focus on the dropped item so keyboard Tab can continue to the next dot.
      this.pendingFocusIndex = event.container.data.index
      this.focusBoxItem(event.container.data.index)
    }
  }

  onBoxItemFocus(item: ZoneBoxModel, index: number, tooltip?: MatTooltip) {
    if (this.keyboardDragSourceIndex !== null) {
      this.keyboardDragTargetIndex = index

      const sourceItem = this.items[this.keyboardDragSourceIndex]
      this.hoveringBox.emit(sourceItem?.active ? sourceItem : null)
    } else {
      // Sync the Asset configuration highlight with keyboard focus.
      this.hoveringBox.emit(item?.active ? item : null)
    }

    // Ensure tooltip is shown for keyboard focus.
    const tooltipItem = this.getTooltipItem(index)
    if (tooltipItem && tooltip) {
      tooltip.show()
    }
  }

  onBoxItemBlur(tooltip?: MatTooltip) {
    // Clear highlight when leaving the grid cell (unless a mouse hover is active).
    if (!this.isHovering && !this.isDragging) {
      this.hoveringBox.emit(null)
    }

    tooltip?.hide()
  }

  onBoxItemKeydown(event: KeyboardEvent, zoneBox: ZoneBoxModel, index: number, tooltip?: MatTooltip) {
    const isSpaceKey = event.key === ' ' || event.key === 'Spacebar' || event.code === 'Space'
    const isEnterKey = event.key === 'Enter' || event.code === 'Enter'
    const isActivationKey = isEnterKey || isSpaceKey

    // If we are in a keyboard drag session, keep the drop target synced with focus
    // only when the user tabs to a different box item (arrow keys handle target separately).
    if (this.keyboardDragSourceIndex !== null && this.keyboardDragTargetIndex === null) {
      this.keyboardDragTargetIndex = index
    }

    if (this.keyboardDragSourceIndex !== null) {
      const handledArrow = this.handleKeyboardDragArrowKeys(event)
      if (handledArrow) {
        return
      }
    }

    if (event.key === 'Escape' && this.keyboardDragSourceIndex !== null) {
      event.preventDefault()
      event.stopPropagation()
      this.clearKeyboardDrag()
      return
    }

    if (!isActivationKey) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    // Start keyboard drag only from an active (draggable) zone box.
    if (this.keyboardDragSourceIndex === null) {
      if (!zoneBox.active) {
        return
      }
      this.keyboardDragSourceIndex = index
      this.keyboardDragTargetIndex = index
      this.isDragging = true

      // Do not show tooltip for the dragged dot.
      tooltip?.hide()

      const sourceItem = this.items[index]
      this.hoveringBox.emit(sourceItem?.active ? sourceItem : null)
      return
    }

    // Drop (swap) onto the current keyboard target cell.
    const sourceIndex = this.keyboardDragSourceIndex
    const targetIndex = this.keyboardDragTargetIndex ?? index

    if (sourceIndex === targetIndex) {
      this.clearKeyboardDrag()
      return
    }

    this.swapItems(sourceIndex, targetIndex)
    this.clearKeyboardDrag()

    // Keep focus on the dropped (target) cell so Tab can continue.
    this.pendingFocusIndex = targetIndex
    this.focusBoxItem(targetIndex)
  }

  private handleKeyboardDragArrowKeys(event: KeyboardEvent): boolean {
    if (this.keyboardDragSourceIndex === null || this.keyboardDragTargetIndex === null) {
      return false
    }

    const key = event.key
    const isArrowKey = key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown'
    if (!isArrowKey) {
      return false
    }

    event.preventDefault()
    event.stopPropagation()

    const currentIndex = this.keyboardDragTargetIndex
    const currentRow = Math.floor(currentIndex / this.zoneMapWidth)
    const currentCol = currentIndex % this.zoneMapWidth

    let nextRow = currentRow
    let nextCol = currentCol

    switch (key) {
      case 'ArrowLeft':
        nextCol = Math.max(0, currentCol - 1)
        break
      case 'ArrowRight':
        nextCol = Math.min(this.zoneMapWidth - 1, currentCol + 1)
        break
      case 'ArrowUp':
        nextRow = Math.max(0, currentRow - 1)
        break
      case 'ArrowDown':
        nextRow = Math.min(this.zoneMapHeight - 1, currentRow + 1)
        break
    }

    const nextIndex = nextRow * this.zoneMapWidth + nextCol
    if (nextIndex !== currentIndex) {
      this.keyboardDragTargetIndex = nextIndex

      // Visually move the dragged dot by moving focus to the destination cell.
      this.focusBoxItem(nextIndex)

      const sourceItem = this.items[this.keyboardDragSourceIndex]
      // Keep the Asset Configuration column hovered on the dragged item while dragging,
      // regardless of whether the destination is empty or already occupied.
      this.hoveringBox.emit(sourceItem?.active ? sourceItem : null)
      this.cdRef.detectChanges()
    }
    return true
  }

  getRenderedItem(index: number): ZoneBoxModel | null {
    const item = this.items[index]
    if (!item) {
      return null
    }

    if (this.keyboardDragSourceIndex === null || this.keyboardDragTargetIndex === null) {
      return item.active ? item : null
    }

    // When dragging with keyboard, show the dragged dot at the current target cell.
    if (index === this.keyboardDragTargetIndex) {
      const sourceItem = this.items[this.keyboardDragSourceIndex]
      if (!sourceItem?.active) {
        return null
      }
      // Preserve the target cell position but display the dragged zone's content.
      return {
        ...item,
        active: true,
        isDuplicate: !!sourceItem.isDuplicate,
        name: sourceItem.name,
        configZoneName: sourceItem.configZoneName,
      } as ZoneBoxModel
    }

    // Hide the source dot while it is being "carried".
    if (index === this.keyboardDragSourceIndex && this.keyboardDragTargetIndex !== this.keyboardDragSourceIndex) {
      return null
    }

    return item.active ? item : null
  }

  getTooltipItem(index: number): ZoneBoxModel | null {
    const item = this.items[index]
    if (!item?.active) {
      return null
    }

    // While keyboard dragging, suppress tooltip for the dragged dot (source).
    if (this.keyboardDragSourceIndex !== null && index === this.keyboardDragSourceIndex) {
      return null
    }

    // Otherwise, show the real cell's tooltip (e.g., when destination has its own dot).
    return item
  }

  getBoxItemAriaLabel(index: number): string {
    const cellItem = this.items[index]
    const rendered = this.getRenderedItem(index)

    const position = `Row ${cellItem.positionY + 1}, Column ${cellItem.positionX + 1}`
    const cellName = rendered ? `Zone ${rendered.name}` : 'Empty cell'

    const grabbedItem = this.keyboardDragSourceIndex !== null && this.items[this.keyboardDragSourceIndex]
      ? this.items[this.keyboardDragSourceIndex]
      : null
    const grabbedName = grabbedItem?.active ? `Zone ${grabbedItem.name}` : 'item'

    if (this.keyboardDragSourceIndex === index) {
      return `${position}. ${cellName}. Grabbed ${grabbedName}. Use arrow keys to choose a drop target, press Enter or Space to drop, or Escape to cancel.`
    }

    if (this.keyboardDragSourceIndex !== null) {
      const isTarget = this.keyboardDragTargetIndex === index
      return isTarget
        ? `${position}. ${cellName}. Current drop target for ${grabbedName}. Press Enter or Space to drop here, or Escape to cancel.`
        : `${position}. ${cellName}. Possible drop target for ${grabbedName}. Use arrow keys to move the drop target, then press Enter or Space to drop.`
    }

    return rendered
      ? `${position}. ${cellName}. Press Enter or Space to grab for drag and drop.`
      : `${position}. ${cellName}. Not draggable.`
  }

  private focusBoxItem(index: number) {
    // Ensure view has updated before focusing.
    setTimeout(() => {
      const els = this.boxItemEls ? this.boxItemEls.toArray() : []
      const el = els[index] ? els[index].nativeElement : null
      el?.focus()
    })
  }

  private clearKeyboardDrag() {
    this.keyboardDragSourceIndex = null
    this.keyboardDragTargetIndex = null
    this.onDragEnd()
  }

  private swapItems(sourceIndex: number, targetIndex: number) {
    const newValue = this.items[targetIndex]
    const oldValue = this.items[sourceIndex]

    this.dropValue.emit({ newValue: newValue, oldValue: oldValue })

    const temp = this.items[sourceIndex]
    this.items[sourceIndex] = this.items[targetIndex]
    this.items[targetIndex] = temp
  }

  onDragStart(zoneBox: ZoneBoxModel) {
    this.keyboardDragSourceIndex = null
    this.keyboardDragTargetIndex = null
    this.isDragging = zoneBox.active
  }

  onDragEnd() {
    this.keyboardDragSourceIndex = null
    this.keyboardDragTargetIndex = null
    this.isDragging = false
    if (!this.isHovering) {
      this.hoveringBox.emit(null)
    }
  }


  rebuildGridZone() {
    setTimeout(() => {
      if (this.svgBackground && this._zoneData && this.svgSource && this.selectedInspectionTypes) {

        this.items = []

        if (this.isAssetViewGridHidden) {
          this.backgroundHeight = this.svgBackground.backgroundHeight

          const calculatedZoneBoxHeight = Math.ceil(this.backgroundHeight / this.zoneMapHeight)
          this.zoneBoxHeight = Math.min(calculatedZoneBoxHeight, this.zoneBoxHeight)
          this.zoneBoxActiveSize = Math.min(this.zoneBoxHeight, this.zoneBoxActiveSize)

        } else {
          this.backgroundWidth = this.svgBackground.backgroundWidth
          this.backgroundHeight = this.svgBackground.backgroundHeight
          this.zoneBoxHeight = this.backgroundHeight / this.zoneMapHeight
          this.zoneBoxWidth = this.backgroundWidth / this.zoneMapWidth

          if (this.zoneBoxHeight > this.zoneBoxWidth) {
            this.zoneBoxActiveSize = this.zoneBoxWidth > 24 ? 24 : this.zoneBoxWidth
          } else {
            this.zoneBoxActiveSize = this.zoneBoxHeight > 24 ? 24 : this.zoneBoxHeight
          }
        }

        for (let i = 0; i < this.zoneMapHeight * this.zoneMapWidth; i++) {
          const positionY = Math.floor(i / this.zoneMapWidth)
          const positionX = i % this.zoneMapWidth
          this.items.push(
            {
              positionX: positionX,
              positionY: positionY,
              active: false,
              isDuplicate: false,
            } as ZoneBoxModel)
        }

        this._zoneData.configZones.forEach(zone => {
          const positionX = zone.assetViewLocation[GRID_VIEW_POSITION_INDEX.POSITION_X]
          const positionY = zone.assetViewLocation[GRID_VIEW_POSITION_INDEX.POSITION_Y]
          const gridIndex = positionY * this.zoneMapWidth + positionX
          const tagTypeId = this.langDictionaryService.convertLangKeyToString(zone.tagTypeLangKey, this.languageDictionary)
          const tagType = TAG_TYPE_OPTIONS.find(option => option.id === tagTypeId)
          const tagTypeName = this.translateService.instant(tagType ? tagType.displayName : 'TAG_TYPE_OPTIONS.ZONE').slice(0,1)
          const zoneName = `${this.langDictionaryService.convertLangKeyToString(zone.tagLangKey, this.languageDictionary)} (${tagTypeName}${zone.tagNumber})`

          if (positionX >= this.zoneMapWidth || positionY >= this.zoneMapHeight) {
            return
          }

          if (zone.zoneInspectionTypes && zone.zoneInspectionTypes.findIndex(x => this.selectedInspectionTypes.includes(x)) > -1) {
            if (this.items[gridIndex] && this.items[gridIndex].active) {
              this.items[gridIndex].isDuplicate = true
              this.items[gridIndex].name += `, ${tagTypeName}${zone.tagNumber}`
              this.items[gridIndex].configZoneName += `,\n${zoneName}`
            } else {
              this.items[gridIndex] = {
                positionX: positionX,
                positionY: positionY,
                active: true,
                name: `${tagTypeName}${zone.tagNumber}`,
                configZoneName: zoneName,
              } as ZoneBoxModel
            }
          }
        })

        this.preparingData.emit(false)

        // After rebuilding the grid (which recreates DOM elements), restore focus to the
        // last dropped position so keyboard users can keep tabbing.
        if (this.pendingFocusIndex !== null) {
          const indexToFocus = this.pendingFocusIndex
          this.pendingFocusIndex = null
          this.focusBoxItem(indexToFocus)
        }
      }
    }, 200)
  }


  onMouseOut() {
    if (!this.isDragging) {
      this.hoveringBox.emit(null)
    }
    this.isHovering = false
  }

  onMouseEnter(boxItem: ZoneBoxModel) {
    if (!this.isDragging) {
      this.hoveringBox.emit(boxItem)
    }
    this.isHovering = true
  }

  handleAfterSVGLoaded(status: boolean) {
    this.rebuildGridZone()
    this.cdRef.detectChanges()
  }

  calculateZoneBoxWidth(zoneBoxItem: ZoneBoxModel): number {
    if (this.isAssetViewGridHidden) {
      return this.zoneBoxWidth
    }

    // Decrease the width of the zone box item if it's the last zone box item in row
    // to avoid the last zone box item being moved to the next row.
    return zoneBoxItem.positionX < this.zoneMapWidth - 1
      ? this.zoneBoxWidth
      : this.zoneBoxWidth - 1
  }
}
